import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Plus, Trash2 } from 'lucide-react'
import { formatCurrency } from '../../utils'

export interface CreditItem {
  id: string
  verba: string
  value: number
}

interface CreditItemsTableProps {
  items: CreditItem[]
  onItemsChange: (items: CreditItem[]) => void
}

export function CreditItemsTable({ items, onItemsChange }: CreditItemsTableProps) {
  const [newItem, setNewItem] = useState({ verba: '', value: '' })

  const addItem = () => {
    if (newItem.verba.trim() && newItem.value.trim()) {
      const item: CreditItem = {
        id: Date.now().toString(),
        verba: newItem.verba.trim(),
        value: Number.parseFloat(newItem.value.replace(',', '.')) || 0
      }
      onItemsChange([...items, item])
      setNewItem({ verba: '', value: '' })
    }
  }

  const updateItem = (id: string, field: keyof CreditItem, value: string | number) => {
    onItemsChange(items.map(item => (item.id === id ? { ...item, [field]: value } : item)))
  }

  const deleteItem = (id: string) => {
    onItemsChange(items.filter(item => item.id !== id))
  }

  const total = items.reduce((sum, item) => sum + item.value, 0)

  const handleValueChange = (id: string, value: string) => {
    const cleanValue = value.replace(/[^\d,]/g, '')
    const numericValue = Number.parseFloat(cleanValue.replace(',', '.')) || 0
    updateItem(id, 'value', numericValue)
  }

  const handleNewValueChange = (value: string) => {
    const cleanValue = value.replace(/[^\d,]/g, '')
    setNewItem({ ...newItem, value: cleanValue })
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Verba</TableHead>
              <TableHead>Valor de Pagamento</TableHead>
              <TableHead className="w-16">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                <TableCell>
                  <Input
                    value={item.verba}
                    onChange={e => updateItem(item.id, 'verba', e.target.value)}
                    placeholder="Digite a verba"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="text"
                    value={item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    onChange={e => handleValueChange(item.id, e.target.value)}
                    placeholder="R$ 0,00"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </TableCell>
                <TableCell>
                  <Button variant="destructive" size="sm" onClick={() => deleteItem(item.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <Input
                  value={newItem.verba}
                  onChange={e => setNewItem({ ...newItem, verba: e.target.value })}
                  placeholder="Nova verba"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="text"
                  value={newItem.value}
                  onChange={e => handleNewValueChange(e.target.value)}
                  placeholder="R$ 0,00"
                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </TableCell>
              <TableCell>
                <Button onClick={addItem} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {items.length > 0 && (
        <div className="text-right">
          <p className="text-lg font-semibold">Total: {formatCurrency(total)}</p>
        </div>
      )}
    </div>
  )
}
